"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import {
	clearNameTag,
	isValidPin,
	readNameTag,
	writeNameTag,
	type NameTag,
} from "@/site/nametag";

export default function StartPage() {
	const [tag, setTag] = useState<NameTag | null>(null);
	const [name, setName] = useState("");
	const [pin, setPin] = useState("");
	const [error, setError] = useState("");
	const [ready, setReady] = useState(false);

	useEffect(() => {
		setTag(readNameTag());
		setReady(true);
	}, []);

	const handleEnter = (event: React.FormEvent) => {
		event.preventDefault();
		if (!name.trim()) {
			setError("이름을 적어주세요");
			return;
		}
		if (!isValidPin(pin)) {
			setError("숫자 여섯 자리로 적어주세요");
			return;
		}
		const next = { name: name.trim(), pin };
		writeNameTag(next);
		setTag(next);
		setError("");
	};

	if (!ready) return null;

	return (
		<div className="min-h-screen bg-neutral-950 text-neutral-100">
			<div className="mx-auto flex min-h-screen max-w-[980px] flex-col justify-center px-6 py-16">
				<div className="mb-10">
					<p className="text-sm font-bold tracking-widest text-blue-400">
						에딧올
					</p>
					<h1 className="mt-3 text-4xl font-extrabold tracking-tight md:text-5xl">
						{tag ? `${tag.name}님, 뭐부터 하실까요` : "이름부터 적어주세요"}
					</h1>
					<p className="mt-4 text-neutral-400">
						{tag
							? "만든 것은 이 컴퓨터에 남습니다. 다음에 같은 이름으로 들어오면 이어서 하시면 됩니다."
							: "강의장 컴퓨터를 여러 분이 같이 쓰기 때문에, 누구 작업인지 구분하려고 받습니다."}
					</p>
				</div>

				{!tag ? (
					<form onSubmit={handleEnter} className="max-w-[420px] space-y-4">
						<label className="block">
							<span className="text-xs text-neutral-400">이름</span>
							<input
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="김민수"
								className="mt-1 w-full rounded-lg border border-white/15 bg-neutral-900 px-4 py-3 text-lg outline-none focus:border-blue-500"
							/>
						</label>
						<label className="block">
							<span className="text-xs text-neutral-400">
								숫자 여섯 자리 (기억하기 쉬운 걸로)
							</span>
							<input
								value={pin}
								inputMode="numeric"
								maxLength={6}
								onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
								placeholder="000000"
								className="mt-1 w-full rounded-lg border border-white/15 bg-neutral-900 px-4 py-3 text-lg tracking-[0.3em] outline-none focus:border-blue-500"
							/>
						</label>
						{error && <p className="text-sm text-amber-400">{error}</p>}
						<button
							type="submit"
							className="w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-bold hover:bg-blue-500"
						>
							시작하기
						</button>
						<p className="text-xs leading-relaxed text-neutral-500">
							이 이름표는 <b>이 컴퓨터 안에만</b> 저장됩니다. 어디로도 보내지
							않습니다. 진짜 비밀번호가 아니니 평소 쓰시는 번호는 적지 마세요.
						</p>
					</form>
				) : (
					<>
						<div className="grid gap-5 md:grid-cols-2">
							<Link
								href="/cardnews"
								className="group rounded-2xl border border-white/12 bg-neutral-900/60 p-8 transition hover:border-blue-500"
							>
								<div className="text-3xl">🖼️</div>
								<h2 className="mt-4 text-2xl font-extrabold">카드뉴스 만들기</h2>
								<p className="mt-2 leading-relaxed text-neutral-400">
									사진 한 장에 글자 두 줄. 인스타·당근에 올릴 <b>사진</b>을
									만듭니다.
								</p>
								<p className="mt-4 text-sm text-blue-400">
									템플릿 10종 · 한글 글씨체 58종
								</p>
							</Link>

							<Link
								href="/projects"
								className="group rounded-2xl border border-white/12 bg-neutral-900/60 p-8 transition hover:border-blue-500"
							>
								<div className="text-3xl">🎬</div>
								<h2 className="mt-4 text-2xl font-extrabold">영상 편집하기</h2>
								<p className="mt-2 leading-relaxed text-neutral-400">
									찍은 영상을 자르고 자막을 얹습니다. 릴스·쇼츠에 올릴{" "}
									<b>세로 영상</b>을 만듭니다.
								</p>
								<p className="mt-4 text-sm text-blue-400">
									세로 30초 · 워터마크 없음
								</p>
							</Link>
						</div>

						<button
							type="button"
							onClick={() => {
								clearNameTag();
								setTag(null);
								setName("");
								setPin("");
							}}
							className="mt-8 self-start text-sm text-neutral-500 underline hover:text-neutral-300"
						>
							다른 사람이 쓸 거예요 (이름표 지우기)
						</button>
					</>
				)}
			</div>
		</div>
	);
}
